import { useEffect, useState } from "react";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import { ClockCircleOutlined, MessageOutlined } from "@ant-design/icons";
import { Button, Modal, Tabs, Tag, Timeline } from "antd";
import {
  deleteLeadNote,
  deleteLeadRemarks,
  getAllLeadRemarks,
  getLeadNoteDetails,
  getLeadRemarksDetails,
} from "../../actionCreator/leadDetailsActionCreator";
import Loader from "../loader";
import deleteIcon from "../../assets/images/webToLead/delete.svg";
import editIcon from "../../assets/svg/icons/edit.svg";
import pinIcon from "../../assets/svg/icons/pin.svg";
import starIcon from "../../assets/svg/icons/star.svg";
import collapse from "../../assets/svg/icons/collapse-activities.svg";
import FeatureComingSoon from "../../common/FeatureComingSoon";
import { Sparkles } from "lucide-react";
import AttractiveEmptyState from "../../common/AttractiveEmptyState";
import { ReactComponent as Call } from "../../assets/svg/icons/call.svg";
import { ReactComponent as ViewEmail } from "../../assets/svg/icons/email.svg";
import { ReactComponent as Meeting } from "../../assets/svg/icons/meeting.svg";
import { ReactComponent as Followup } from "../../assets/svg/icons/followup.svg";
import { ReactComponent as EditIcon } from "../../assets/svg/icons/edit-action.svg";
import { ReactComponent as TrashIcon } from "../../assets/svg/icons/trash-action.svg";
import { ReactComponent as PinIcon } from "../../assets/svg/icons/pin-action.svg";
import { ReactComponent as StarIcon } from "../../assets/svg/icons/star-action.svg";
import { ReactComponent as ChevronDownIcon } from "../../assets/svg/icons/chevron-down-action.svg";
import { ReactComponent as Whatsapp } from "../../assets/svg/icons/whatsapp.svg";
import { ReactComponent as Chat } from "../../assets/svg/icons/chat.svg";
import { formatSmartDate } from "../../utils";

const ActivityTime = ({ setActiveTab, setEditTask, setAddTask }) => {
  const dispatch = useDispatch();
  const { confirm } = Modal;
  const { id } = useParams();
  const leadRemarks = useSelector(
    (state) => state.LeadDetailsReducer.leadRemarks,
  );
  const leadDetail = useSelector(
    (state) => state.LeadDetailsReducer.leadDetails,
  );
  const loading = useSelector((state) => state.LeadDetailsReducer.loading);

  const getActiveTab = (id, type) => {
    if (type === "deletenote") {
      showConfirm(id, leadDetail?.id, "note");
    } else if (type === "deleteremarks") {
      showConfirm(id, leadDetail?.id, "task");
    } else {
      if (type === "editnote") {
        dispatch(getLeadNoteDetails(id));
      } else if (type === "editremarks") {
        dispatch(getLeadRemarksDetails(id));
      }
    }
  };

  function showConfirm(id, leadId, type) {
    confirm({
      title: "Do you want to delete this " + type + "?",
      content: "",
      onOk() {
        return new Promise((resolve, reject) => {
          setTimeout(Math.random() > 0.5 ? resolve : reject, 1000);
          if (type === "note") {
            dispatch(deleteLeadNote(id, leadId));
          } else {
            dispatch(deleteLeadRemarks(id, leadId));
          }
        }).catch(() => console.log("Oops errors!"));
      },
      onCancel() {},
    });
  }

  useEffect(() => {
    if (id) {
      dispatch(getAllLeadRemarks(id));
    }
  }, [dispatch, id]);

  const handleDelete = (id) => {
    getActiveTab(id, "deleteremarks");
  };

  const timelineIconMap = {
    call: {
      icon: <Call />,
      colorClass: "tw-text-blue-500",
      bgClass: "tw-bg-blue-50",
      borderClass: "tw-border-blue-100",
    },
    email: {
      icon: <ViewEmail />,
      colorClass: "tw-text-yellow-600",
      bgClass: "tw-bg-yellow-50",
      borderClass: "tw-border-yellow-100",
    },
    meeting: {
      icon: <Meeting />,
      colorClass: "tw-text-red-500",
      bgClass: "tw-bg-red-50",
      borderClass: "tw-border-red-100",
    },
    followup: {
      icon: <Followup />,
      colorClass: "tw-text-indigo-500",
      bgClass: "tw-bg-indigo-50",
      borderClass: "tw-border-indigo-100",
    },
    chat: {
      icon: <Chat />,
      colorClass: "tw-text-emerald-500",
      bgClass: "tw-bg-emerald-50",
      borderClass: "tw-border-emerald-100",
    },
    whatsapp: {
      icon: <Whatsapp />,
      colorClass: "tw-text-violet-500",
      bgClass: "tw-bg-violet-50",
      borderClass: "tw-border-violet-100",
    },
  };

  const getTimelineDot = (type, onClick) => {
    const config = timelineIconMap[type] || {
      icon: <MessageOutlined className="tw-text-xs" />,
      colorClass: "tw-text-slate-500",
      bgClass: "tw-bg-slate-50",
      borderClass: "tw-border-slate-200",
    };

    return (
      <div
        onClick={onClick}
        className={`tw-size-8 tw-rounded-full tw-border tw-border-solid tw-flex tw-items-center tw-justify-center tw-shadow-sm ${config.colorClass} ${config.bgClass} ${config.borderClass} ${
          onClick
            ? "tw-cursor-pointer hover:tw-scale-105 hover:tw-shadow tw-transition-all"
            : ""
        }`}
      >
        <span className="tw-size-4 tw-flex tw-items-center tw-justify-center">
          {config.icon}
        </span>
      </div>
    );
  };

  const timelineItems = leadRemarks?.map((item) => {
    const currentTaskType = item?.type || item?.title;
    const isEmailType = currentTaskType?.toLowerCase() === "email";
    const handleDotClick = isEmailType ? () => setActiveTab("4") : undefined;
    return {
      key: item.id,
      dot: getTimelineDot(currentTaskType, handleDotClick),
      children: (
        <ActivityCard
          item={item}
          setEditTask={setEditTask}
          handleDelete={handleDelete}
          setActiveTab={setActiveTab}
        />
      ),
    };
  });

  if (loading) {
    return <Loader />;
  }

  return (
    <div>
      {/* <div className="d-flex align-items-center justify-content-end gap-20">
        <Select
          mode="multiple"
          allowClear
          showSearch
          value={selected}
          onChange={handleChange}
          className="tw-min-w-[200px]"
          placeholder="Search by Tags"
          options={tags.map((tag) => ({
            value: tag?.name,
            label: tag?.name,
          }))}
        />

        <Select
          className="leadBtn roboto-medium"
          placeholder="Activities"
          defaultValue={null}
          options={options}
          onChange={(value) => {
            setActivity(value);
            dispatch(getAllLeadRemarks(id, { activity: value }));
          }}
        />
      </div> */}

      <Tabs
        className="activities-tab"
        items={[
          {
            key: "1",
            label: (
              <span className="px-30 tw-flex tw-items-center tw-gap-2">
                Show All ({leadRemarks?.length})
              </span>
            ),
            children: leadRemarks?.length ? (
              <Timeline className="tw-mt-7 tw-mx-7" items={timelineItems} />
            ) : (
              <>
                <div className="d-flex mt30 flex-column text-center gap-10 align-items-center">
                  <div className="fw-semibold">No Activities Scheduled</div>
                  <div className="text-gray tw-max-w-[400px]">
                    Your scheduled activities, pinned reminders, email drafts,
                    and queued emails will show up here.
                  </div>
                  <Button
                    type="link"
                    className="tw-text-theme fw-bold"
                    onClick={() => {
                      setActiveTab("3");
                      setAddTask();
                    }}
                  >
                    <i className="fa fa-plus" aria-hidden="true"></i>Schedule an
                    activity
                  </Button>
                </div>
              </>
            ),
          },
          // {
          //   key: "2",
          //   label: (
          //     <span className="px-30 tw-flex tw-items-center tw-gap-2">
          //       Calls
          //     </span>
          //   ),
          //   children: leadRemarks?.length ? (
          //     <Timeline className="tw-mt-7 tw-mx-7" items={timelineItems} />
          //   ) : (
          //     <div className="tw-my-10 tw-flex tw-justify-center">
          //       <img src={noData} alt="" />
          //     </div>
          //   ),
          // },
          // {
          //   key: "3",
          //   label: (
          //     <span className="px-30 tw-flex tw-items-center tw-gap-2">
          //       Emails
          //     </span>
          //   ),
          //   children: leadRemarks?.length ? (
          //     <Timeline className="tw-mt-7 tw-mx-7" items={timelineItems} />
          //   ) : (
          //     <div className="tw-my-10 tw-flex tw-justify-center">
          //       <img src={noData} alt="" />
          //     </div>
          //   ),
          // },
          // {
          //   key: "4",
          //   label: (
          //     <span className="px-30 tw-flex tw-items-center tw-gap-2">
          //       Meetings
          //     </span>
          //   ),
          //   children: leadRemarks?.length ? (
          //     <Timeline className="tw-mt-7 tw-mx-7" items={timelineItems} />
          //   ) : (
          //     <div className="tw-my-10 tw-flex tw-justify-center">
          //       <img src={noData} alt="" />
          //     </div>
          //   ),
          // },
          // {
          //   key: "5",
          //   label: (
          //     <span className="px-30 tw-flex tw-items-center tw-gap-2">
          //       Follow up
          //     </span>
          //   ),
          //   children: leadRemarks?.length ? (
          //     <Timeline className="tw-mt-7 tw-mx-7" items={timelineItems} />
          //   ) : (
          //     <div className="tw-my-10 tw-flex tw-justify-center">
          //       <img src={noData} alt="" />
          //     </div>
          //   ),
          // },
          // {
          //   key: "6",
          //   label: (
          //     <span className="px-30 tw-flex tw-items-center tw-gap-2">
          //       Chats
          //     </span>
          //   ),
          //   children: leadRemarks?.length ? (
          //     <Timeline className="tw-mt-7 tw-mx-7" items={timelineItems} />
          //   ) : (
          //     <div className="tw-my-10 tw-flex tw-justify-center">
          //       <img src={noData} alt="" />
          //     </div>
          //   ),
          // },
          // {
          //   key: "7",
          //   label: (
          //     <span className="px-30 tw-flex tw-items-center tw-gap-2">
          //       Whatsapp
          //     </span>
          //   ),
          //   children: leadRemarks?.length ? (
          //     <Timeline className="tw-mt-7 tw-mx-7" items={timelineItems} />
          //   ) : (
          //     <div className="tw-my-10 tw-flex tw-justify-center">
          //       <img src={noData} alt="" />
          //     </div>
          //   ),
          // },
          {
            key: "8",
            label: (
              <FeatureComingSoon>
                <span className="px-30 tw-flex tw-items-center tw-gap-2">
                  Starred
                </span>
              </FeatureComingSoon>
            ),
            children: (
              <AttractiveEmptyState
                icon={Sparkles}
                title="No starred activities"
                description="Starred activities and interactions will appear here."
              />
            ),
          },
        ]}
      />
    </div>
  );
};

export default ActivityTime;

const ActivityCard = ({ item, setEditTask, handleDelete, setActiveTab }) => {
  const [isCollapse, setCollapse] = useState(false);
  const currentTaskType = item?.type || item?.title;
  const currentTaskTitle = item?.title || currentTaskType;
  const isEmailType = currentTaskType?.toLowerCase() === "email";

  return (
    <div className="tw-ml-4 tw-group tw-bg-white hover:tw-shadow-sm tw-border tw-border-solid tw-border-slate-200/80 tw-py-4 tw-px-5 tw-mt-4 tw-rounded-2xl tw-transition-all">
      <div className="tw-flex tw-items-start tw-justify-between tw-gap-4 tw-mb-2">
        <div
          onClick={isEmailType ? () => setActiveTab("4") : undefined}
          className={`tw-font-bold tw-text-sm tw-leading-snug ${
            isEmailType
              ? "tw-text-slate-800 hover:tw-text-theme tw-cursor-pointer tw-transition-colors"
              : "tw-text-slate-800"
          }`}
        >
          {currentTaskTitle}
        </div>
        <div className="tw-flex tw-items-center tw-gap-1.5 tw-shrink-0">
          <div className="tw-opacity-0 group-hover:tw-opacity-100 tw-flex tw-items-center tw-justify-end tw-gap-1.5 tw-transition-opacity tw-duration-150">
            <button
              type="button"
              className="tw-flex tw-items-center tw-justify-center tw-size-8 tw-rounded-full tw-border tw-border-solid tw-border-slate-200 hover:tw-border-blue-400 hover:tw-text-blue-500 hover:tw-bg-blue-50/30 tw-bg-white tw-text-slate-450 tw-cursor-pointer tw-transition-all tw-outline-none"
              onClick={(e) => {
                e.stopPropagation();
                setEditTask(item);
              }}
            >
              <EditIcon className="tw-size-3.5" />
            </button>

            <button
              type="button"
              className="tw-flex tw-items-center tw-justify-center tw-size-8 tw-rounded-full tw-border tw-border-solid tw-border-slate-200 hover:tw-border-rose-350 hover:tw-text-rose-500 hover:tw-bg-rose-50/30 tw-bg-white tw-text-slate-450 tw-cursor-pointer tw-transition-all tw-outline-none"
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(item.id);
              }}
            >
              <TrashIcon className="tw-size-3.5" />
            </button>

            <FeatureComingSoon>
              <button
                type="button"
                className="tw-flex tw-items-center tw-justify-center tw-size-8 tw-rounded-full tw-border tw-border-solid tw-border-slate-200 hover:tw-border-slate-350 hover:tw-text-slate-750 hover:tw-bg-slate-50 tw-bg-white tw-text-slate-450 tw-cursor-pointer tw-transition-all tw-outline-none"
              >
                <PinIcon className="tw-size-3.5" />
              </button>
            </FeatureComingSoon>
          </div>

          <FeatureComingSoon>
            <button
              type="button"
              className="tw-flex tw-items-center tw-justify-center tw-size-8 tw-rounded-full tw-border tw-border-solid tw-border-slate-200 hover:tw-border-amber-350 hover:tw-text-amber-550 hover:tw-bg-amber-50/40 tw-bg-white tw-text-slate-450 tw-cursor-pointer tw-transition-all tw-outline-none"
            >
              <StarIcon className="tw-size-3.5" />
            </button>
          </FeatureComingSoon>

          <button
            type="button"
            className="tw-flex tw-items-center tw-justify-center tw-size-8 tw-rounded-full tw-border tw-border-solid tw-border-slate-200 hover:tw-border-slate-350 hover:tw-text-slate-750 hover:tw-bg-slate-50 tw-bg-white tw-text-slate-450 tw-cursor-pointer tw-transition-all tw-outline-none"
            onClick={(e) => {
              e.stopPropagation();
              setCollapse(!isCollapse);
            }}
          >
            <ChevronDownIcon
              className={`tw-size-3.5 tw-transition-transform tw-duration-200 ${isCollapse ? "tw-rotate-180" : ""}`}
            />
          </button>
        </div>
      </div>

      {!isCollapse && (
        <div className="tw-transition-all">
          {item?.remarks && (
            <p className="tw-text-xs tw-text-slate-500 tw-leading-relaxed tw-whitespace-pre-line tw-mb-4 tw-mt-1">
              {item?.remarks}
            </p>
          )}
          <div className="tw-flex tw-items-center tw-justify-between tw-flex-wrap tw-gap-3 tw-border-0 tw-border-t tw-border-solid tw-border-slate-100 tw-pt-3">
            <div className="tw-flex tw-items-center tw-text-[11px] tw-text-slate-400 tw-gap-1.5">
              <ClockCircleOutlined className="tw-text-slate-400" />
              <span>{formatSmartDate(item.interactionDate)}</span>
              <span className="tw-text-slate-300 tw-select-none">•</span>
              <span>{moment(item.interactionDate).format("MMM DD, YYYY")}</span>
            </div>

            <div className="tw-flex tw-items-center tw-gap-2">
              {item?.status && (
                <span className="tw-text-[10px] tw-font-bold tw-px-2 tw-py-0.5 tw-rounded-md tw-tracking-wide tw-uppercase tw-bg-indigo-50 tw-text-theme tw-border tw-border-solid tw-border-indigo-100/85">
                  {item.status}
                </span>
              )}

              {!!item?.tags?.length && (
                <div className="tw-flex tw-items-center tw-flex-wrap tw-gap-1">
                  {item?.tags?.map((tag) => (
                    <Tag
                      key={tag?.id || tag?.name}
                      className="tw-rounded-full tw-bg-slate-50 tw-border-slate-150 tw-text-[10px] tw-font-semibold tw-text-slate-500 tw-py-0.5 tw-px-2 tw-m-0"
                    >
                      {tag?.name}
                    </Tag>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
